import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Sun, Moon, Plus, Activity, Brain, Zap, Shield,
  Users, MapPin, Coffee, Briefcase, BookOpen, Dumbbell,
  Smartphone, Heart, ShoppingCart, BedDouble, MoreHorizontal,
  Check, Trash2, Clock
} from "lucide-react";
import { toast } from "sonner";

// Activity icons mapping
const ACTIVITY_ICONS = {
  lavoro: Briefcase,
  studio: BookOpen,
  sport: Dumbbell,
  socialita: Users,
  pausa: Coffee,
  procrastinazione: Smartphone,
  cura_personale: Heart,
  commissioni: ShoppingCart,
  riposo: BedDouble,
  altro: MoreHorizontal
};

const EmaPage = () => {
  const [loading, setLoading] = useState(true);
  const [todayCheckins, setTodayCheckins] = useState({ morning: null, evening: null });
  const [todayActivities, setTodayActivities] = useState([]);
  const [categories, setCategories] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Check-in dialog
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [checkinType, setCheckinType] = useState("morning");
  const [checkinData, setCheckinData] = useState({
    mood: 5, stress: 5, energy: 5, control: 5, notes: ""
  });
  
  // Activity dialog
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [newActivity, setNewActivity] = useState({
    activity_type: "",
    social_context: "",
    location_context: "",
    description: "",
    immediate_effect: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [checkinsRes, activitiesRes, categoriesRes, statsRes] = await Promise.all([
        api.get('/ema/checkins/today'),
        api.get('/ema/activities'),
        api.get('/ema/activity-categories'),
        api.get('/ema/stats?days=7')
      ]);
      setTodayCheckins(checkinsRes.data);
      setTodayActivities(activitiesRes.data || []);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching EMA data:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckinSubmit = async () => {
    try {
      await api.post('/ema/checkins', {
        checkin_type: checkinType,
        ...checkinData
      });
      toast.success(`Check-in ${checkinType === 'morning' ? 'mattutino' : 'serale'} salvato!`);
      setCheckinDialogOpen(false);
      setCheckinData({ mood: 5, stress: 5, energy: 5, control: 5, notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleActivitySubmit = async () => {
    if (!newActivity.activity_type) {
      toast.error("Seleziona un'attività");
      return;
    }
    try {
      await api.post('/ema/activities', newActivity);
      toast.success("Attività registrata!");
      setActivityDialogOpen(false);
      setNewActivity({
        activity_type: "", social_context: "", location_context: "",
        description: "", immediate_effect: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await api.delete(`/ema/activities/${activityId}`);
      toast.success("Attività eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openCheckinDialog = (type) => {
    setCheckinType(type);
    const existing = todayCheckins[type];
    if (existing) {
      setCheckinData({
        mood: existing.mood,
        stress: existing.stress,
        energy: existing.energy,
        control: existing.control,
        notes: existing.notes || ""
      });
    } else {
      setCheckinData({ mood: 5, stress: 5, energy: 5, control: 5, notes: "" });
    }
    setCheckinDialogOpen(true);
  };

  const getScoreColor = (score) => {
    if (score >= 7) return "text-green-600";
    if (score >= 4) return "text-yellow-600";
    return "text-red-600";
  };

  const getEffectBadge = (effect) => {
    if (effect === "alleggerente") return <Badge className="bg-green-100 text-green-700">Alleggerente</Badge>;
    if (effect === "faticosa") return <Badge className="bg-red-100 text-red-700">Faticosa</Badge>;
    return <Badge className="bg-gray-100 text-gray-700">Neutra</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="ema-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800">Pattern Benessere</h1>
        <p className="text-gray-500 mt-1">Scopri cosa influenza il tuo benessere</p>
      </div>

      {/* Check-in Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Morning Check-in */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${
            todayCheckins.morning ? 'border-amber-300 bg-amber-50/50' : 'border-dashed border-2'
          }`}
          onClick={() => openCheckinDialog("morning")}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                todayCheckins.morning ? 'bg-amber-200' : 'bg-gray-100'
              }`}>
                <Sun className={`w-6 h-6 ${todayCheckins.morning ? 'text-amber-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="font-semibold">Check-in Mattina</h3>
                <p className="text-sm text-gray-500">
                  {todayCheckins.morning ? 'Completato' : 'Come stai oggi?'}
                </p>
              </div>
              {todayCheckins.morning && <Check className="w-5 h-5 text-green-500 ml-auto" />}
            </div>
            
            {todayCheckins.morning && (
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <div>
                  <p className="text-gray-500">Umore</p>
                  <p className={`font-bold ${getScoreColor(todayCheckins.morning.mood)}`}>
                    {todayCheckins.morning.mood}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Stress</p>
                  <p className={`font-bold ${getScoreColor(11 - todayCheckins.morning.stress)}`}>
                    {todayCheckins.morning.stress}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Energia</p>
                  <p className={`font-bold ${getScoreColor(todayCheckins.morning.energy)}`}>
                    {todayCheckins.morning.energy}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Controllo</p>
                  <p className={`font-bold ${getScoreColor(todayCheckins.morning.control)}`}>
                    {todayCheckins.morning.control}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Evening Check-in */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${
            todayCheckins.evening ? 'border-indigo-300 bg-indigo-50/50' : 'border-dashed border-2'
          }`}
          onClick={() => openCheckinDialog("evening")}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                todayCheckins.evening ? 'bg-indigo-200' : 'bg-gray-100'
              }`}>
                <Moon className={`w-6 h-6 ${todayCheckins.evening ? 'text-indigo-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="font-semibold">Check-in Sera</h3>
                <p className="text-sm text-gray-500">
                  {todayCheckins.evening ? 'Completato' : 'Come è andata oggi?'}
                </p>
              </div>
              {todayCheckins.evening && <Check className="w-5 h-5 text-green-500 ml-auto" />}
            </div>
            
            {todayCheckins.evening && (
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <div>
                  <p className="text-gray-500">Umore</p>
                  <p className={`font-bold ${getScoreColor(todayCheckins.evening.mood)}`}>
                    {todayCheckins.evening.mood}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Stress</p>
                  <p className={`font-bold ${getScoreColor(11 - todayCheckins.evening.stress)}`}>
                    {todayCheckins.evening.stress}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Energia</p>
                  <p className={`font-bold ${getScoreColor(todayCheckins.evening.energy)}`}>
                    {todayCheckins.evening.energy}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Controllo</p>
                  <p className={`font-bold ${getScoreColor(todayCheckins.evening.control)}`}>
                    {todayCheckins.evening.control}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity Logging */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Attività di Oggi</CardTitle>
              <CardDescription>Registra cosa fai per capire cosa ti fa stare bene</CardDescription>
            </div>
            <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> Registra
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md rounded-2xl">
                <DialogHeader>
                  <DialogTitle>Cosa stai facendo?</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-5 pt-4">
                  {/* Activity Type */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Tipo di attività</p>
                    <div className="grid grid-cols-5 gap-2">
                      {categories?.categories.map(cat => {
                        const Icon = ACTIVITY_ICONS[cat.id] || MoreHorizontal;
                        const isSelected = newActivity.activity_type === cat.id;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setNewActivity({ ...newActivity, activity_type: cat.id })}
                            className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-all ${
                              isSelected 
                                ? 'bg-indigo-100 border-2 border-indigo-500' 
                                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-xl">{cat.emoji}</span>
                            <span className="text-[10px] text-gray-600 truncate w-full text-center">{cat.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Social Context */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Con chi?</p>
                    <div className="flex flex-wrap gap-2">
                      {categories?.social_contexts.map(ctx => (
                        <button
                          key={ctx.id}
                          onClick={() => setNewActivity({ ...newActivity, social_context: ctx.id })}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            newActivity.social_context === ctx.id
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {ctx.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Location Context */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Dove?</p>
                    <div className="flex flex-wrap gap-2">
                      {categories?.location_contexts.map(ctx => (
                        <button
                          key={ctx.id}
                          onClick={() => setNewActivity({ ...newActivity, location_context: ctx.id })}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            newActivity.location_context === ctx.id
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {ctx.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Immediate Effect */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Come ti fa sentire questa attività?</p>
                    <div className="flex gap-2">
                      {categories?.immediate_effects.map(eff => (
                        <button
                          key={eff.id}
                          onClick={() => setNewActivity({ ...newActivity, immediate_effect: eff.id })}
                          className={`flex-1 p-3 rounded-xl flex flex-col items-center gap-1 transition-all ${
                            newActivity.immediate_effect === eff.id
                              ? eff.id === 'alleggerente' ? 'bg-green-100 border-2 border-green-500'
                                : eff.id === 'faticosa' ? 'bg-red-100 border-2 border-red-500'
                                : 'bg-gray-200 border-2 border-gray-500'
                              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-2xl">{eff.emoji}</span>
                          <span className="text-xs">{eff.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button onClick={handleActivitySubmit} className="w-full rounded-xl">
                    Registra Attività
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {todayActivities.length > 0 ? (
            <div className="space-y-3">
              {todayActivities.map(activity => {
                const cat = categories?.categories.find(c => c.id === activity.activity_type);
                return (
                  <div 
                    key={activity.activity_id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 group"
                  >
                    <span className="text-2xl">{cat?.emoji || "📌"}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800">{cat?.label || activity.activity_type}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{activity.time}</span>
                        {activity.social_context && (
                          <span>• {categories?.social_contexts.find(s => s.id === activity.social_context)?.label}</span>
                        )}
                        {activity.location_context && (
                          <span>• {categories?.location_contexts.find(l => l.id === activity.location_context)?.label}</span>
                        )}
                      </div>
                    </div>
                    {activity.immediate_effect && getEffectBadge(activity.immediate_effect)}
                    <button 
                      onClick={() => handleDeleteActivity(activity.activity_id)}
                      className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nessuna attività registrata oggi</p>
              <p className="text-sm text-gray-400 mt-1">
                Registra le tue attività per scoprire cosa ti fa stare bene
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weekly Stats */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ultimi 7 giorni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <Sun className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-amber-700">
                  {stats.checkins.averages.morning.mood || "-"}
                </p>
                <p className="text-xs text-gray-500">Umore mattina</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-xl">
                <Moon className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-indigo-700">
                  {stats.checkins.averages.evening.mood || "-"}
                </p>
                <p className="text-xs text-gray-500">Umore sera</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <Zap className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">
                  {stats.checkins.averages.morning.energy || "-"}
                </p>
                <p className="text-xs text-gray-500">Energia media</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <Activity className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-700">
                  {stats.activities.total}
                </p>
                <p className="text-xs text-gray-500">Attività</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Check-in Dialog */}
      <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {checkinType === "morning" ? (
                <><Sun className="w-5 h-5 text-amber-500" /> Check-in Mattina</>
              ) : (
                <><Moon className="w-5 h-5 text-indigo-500" /> Check-in Sera</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Mood */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Umore</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(checkinData.mood)}`}>
                  {checkinData.mood}
                </span>
              </div>
              <Slider
                value={[checkinData.mood]}
                onValueChange={([v]) => setCheckinData({ ...checkinData, mood: v })}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Pessimo</span>
                <span>Ottimo</span>
              </div>
            </div>

            {/* Stress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Stress</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(11 - checkinData.stress)}`}>
                  {checkinData.stress}
                </span>
              </div>
              <Slider
                value={[checkinData.stress]}
                onValueChange={([v]) => setCheckinData({ ...checkinData, stress: v })}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Calmo</span>
                <span>Molto stressato</span>
              </div>
            </div>

            {/* Energy */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Energia</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(checkinData.energy)}`}>
                  {checkinData.energy}
                </span>
              </div>
              <Slider
                value={[checkinData.energy]}
                onValueChange={([v]) => setCheckinData({ ...checkinData, energy: v })}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Scarico</span>
                <span>Pieno di energia</span>
              </div>
            </div>

            {/* Control */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Senso di controllo</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(checkinData.control)}`}>
                  {checkinData.control}
                </span>
              </div>
              <Slider
                value={[checkinData.control]}
                onValueChange={([v]) => setCheckinData({ ...checkinData, control: v })}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Sopraffatto</span>
                <span>In controllo</span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700">Note (opzionale)</label>
              <Textarea
                value={checkinData.notes}
                onChange={(e) => setCheckinData({ ...checkinData, notes: e.target.value })}
                placeholder="Come ti senti? Cosa è successo?"
                className="mt-2 rounded-xl"
                rows={2}
              />
            </div>

            <Button onClick={handleCheckinSubmit} className="w-full rounded-xl">
              Salva Check-in
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmaPage;
